import { useState } from "react";
import { motion } from "framer-motion";
import { Link, Navigate, useParams } from "react-router-dom";
import HeroArticle from "../../../components/common/HeroArticle";
import CardPillLabel from "../../../components/ui/CardPillLabel";
import ImageModal from "../../media/components/ImageModal";
import VideoModal from "../../media/components/VideoModal";
import {
  IconFullscreen, IconOpenExternal,
  IconAnchor, IconHelm, IconShieldCross, IconPillar,
  IconCrown, IconCross, IconStarSaint, IconFlame,
} from "../../../components/ui/Icons";
import { timelineItems } from "../data/timelineItems";

const ENTRY_ICONS = {
  "okno-na-swiat":          { Icon: IconAnchor,      themeColor: "#0891b2" },
  "leonid-teliga":          { Icon: IconHelm,        themeColor: "#0e7490" },
  "sobek":                  { Icon: IconShieldCross, themeColor: "#7c3aed" },
  "grod-kerin":             { Icon: IconPillar,      themeColor: "#1d4ed8" },
  "rzym":                   { Icon: IconCrown,       themeColor: "#d97706" },
  "slowianie-i-wikingowie": { Icon: IconCross,       themeColor: "#15803d" },
  "sw-wojciech":            { Icon: IconStarSaint,   themeColor: "#a21caf" },
  "odwaga":                 { Icon: IconFlame,       themeColor: "#dc6803" },
};

export default function TimelineEntryPage() {
  const { slug } = useParams();
  const [openImg, setOpenImg] = useState(null);
  const [videoOpen, setVideoOpen] = useState(false);

  const entryIndex = timelineItems.findIndex((item) => item.slug === slug);
  const entry = timelineItems[entryIndex];

  if (!entry) return <Navigate to="/w-budowie" replace />;
  if (!entry.ready) return <Navigate to="/w-budowie" replace />;

  const previousEntry = entryIndex > 0 ? timelineItems[entryIndex - 1] : null;
  const nextEntry =
    entryIndex < timelineItems.length - 1 ? timelineItems[entryIndex + 1] : null;

  const navButtons = (
    <>
      {previousEntry && (
        <Link
          to={`/sciezka-czasu/${previousEntry.slug}`}
          className="routePointNavBtn routePointNavPrev"
        >
          ← {previousEntry.title}
        </Link>
      )}

      {nextEntry && (
        <Link
          to={`/sciezka-czasu/${nextEntry.slug}`}
          className="routePointNavBtn routePointNavNext"
        >
          {nextEntry.title} →
        </Link>
      )}
    </>
  );

  return (
    <section className="pageSection pageWithHeaderOffset">
      <div className="routePointNavMobileBar">
        <div className="routePointNavMobileInner">{navButtons}</div>
      </div>

      <div className="container">
        <div className="routePointNavCard routePointNavDesktopCard card">
          <div className="routePointNavHeader">
            <h2 className="routePointNavTitle">Nawigacja po trasie</h2>
            <p className="routePointNavText">
              Przechodź wygodnie do poprzedniego lub następnego punktu ścieżki czasu.
            </p>
          </div>

          <div className="routePointNavActions">{navButtons}</div>
        </div>

        <div className="card cardLabeled timelineEntryHeaderCard">
          <CardPillLabel text="Ścieżka czasu" variant="miejsca" />
          <h1 className="h1">{entry.title}</h1>
          <p className="pageLead">{entry.lead}</p>
        </div>

        <HeroArticle
          imgSrc={entry.image}
          tagText="ETAP OPOWIEŚCI"
          //title={entry.title}
          subtitle={entry.subtitle}
          body={entry.description}
          onImageClick={() => {
            setOpenImg({
              src: entry.image,
              alt: entry.title,
            });
          }}
          childrenBelow={
            <>
              <div className="infoGrid">
                {(() => {
                  const ei = ENTRY_ICONS[entry.slug];
                  return [
                    { label: "Gdzie jesteś", value: entry.pointLabel, iconColor: "#2563eb"        },
                    { label: "Temat",        value: entry.themeLabel,  iconColor: ei?.themeColor  },
                  ].map(({ label, value, iconColor }) => (
                    <motion.div
                      key={`${entry.slug}-${label}`}
                      className="infoBox"
                      initial={{ opacity: 0, y: 24 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, amount: 0.25 }}
                      transition={{ duration: 1.6, ease: "easeOut" }}
                    >
                      {ei && (
                        <span className="infoBoxIcon" style={{ color: iconColor }}>
                          <ei.Icon />
                        </span>
                      )}
                      <div className="infoLabel">{label}</div>
                      <div className="infoValue">{value}</div>
                    </motion.div>
                  ));
                })()}
              </div>

              {entry.content?.length > 0 && (
                <div className="timelineEntryContent">
                  {entry.content.map((paragraph, index) => (
                    <p key={index} className="timelineEntryParagraph">
                      {paragraph}
                    </p>
                  ))}
                </div>
              )}
            </>
          }
        />

        {entry.video && (
          <div className="card cardLabeled">
            <CardPillLabel text="Film" variant="film" />

            <div className="videoWrap" style={{ marginTop: 6 }}>
              <video
                className="videoPlayer"
                src={entry.video}
                controls
                playsInline
                preload="metadata"
              >
                Twoja przeglądarka nie wspiera odtwarzania wideo.
              </video>
            </div>

            <div className="row gap wrap" style={{ marginTop: 10 }}>
              <button
                type="button"
                className="btn btnIcon"
                onClick={() => setVideoOpen(true)}
              >
                <IconFullscreen />
                Otwórz film na pełnym ekranie
              </button>

              {/* <a
                className="btn btnIcon"
                href={entry.video}
                target="_blank"
                rel="noreferrer"
              >
                <IconOpenExternal />
                Otwórz film w nowej karcie
              </a> */}
            </div>
          </div>
        )}
      </div>

      {openImg && (
        <ImageModal
          src={openImg.src}
          alt={openImg.alt}
          onClose={() => setOpenImg(null)}
        />
      )}

      {videoOpen && entry.video && (
        <VideoModal
          src={entry.video}
          title={entry.title}
          onClose={() => setVideoOpen(false)}
        />
      )}
    </section>
  );
}